import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssetManagement } from '@aptsi-types';
import { DataAssetSourceService } from '../services/data-asset-source.service';

interface FileImportDialogData {
  assetSourceId: string;
  assetSourceName: string;
  assetSourceType: string;
  assetSource: string;
}

interface FileFormatConfig {
  label: string;
  accept: string;
  extensions: string[];
}

type AssetRecord = AssetManagement & Record<string, unknown>;

@Component({
  selector: 'app-data-assets-capture-for-file-asset-source',
  templateUrl: './data-assets-capture-for-file-asset-source.component.html',
  styleUrls: ['./data-assets-capture-for-file-asset-source.component.scss'],
})
export class DataAssetsCaptureForFileAssetSourceComponent {
  selectedFile: File | null = null;
  uploading = false;
  fetchingAssets = false;
  assetsLoaded = false;
  importMessage = '';
  errorMessage = '';
  assets: AssetManagement[] = [];

  private readonly formats: Record<string, FileFormatConfig> = {
    csv: { label: 'CSV file', accept: '.csv,text/csv', extensions: ['csv'] },
    excel: {
      label: 'Excel spreadsheet',
      accept: '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
      extensions: ['xlsx', 'xls'],
    },
    xlsx: { label: 'Excel spreadsheet', accept: '.xlsx', extensions: ['xlsx'] },
    xls: { label: 'Excel spreadsheet', accept: '.xls', extensions: ['xls'] },
    json: { label: 'JSON file', accept: '.json,application/json', extensions: ['json'] },
    txt: { label: 'Text file', accept: '.txt,text/plain', extensions: ['txt'] },
    xml: { label: 'XML file', accept: '.xml,application/xml,text/xml', extensions: ['xml'] },
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: FileImportDialogData,
    private readonly dialogRef: MatDialogRef<DataAssetsCaptureForFileAssetSourceComponent>,
    private readonly dataAssetSourceService: DataAssetSourceService,
  ) {}

  get formatConfig(): FileFormatConfig {
    const sourceFormat = String(this.data.assetSource ?? '').trim().toLowerCase().replace(/^\./, '');
    const known = this.formats[sourceFormat];
    if (known) return known;
    if (/^[a-z0-9]{1,12}$/.test(sourceFormat)) {
      return {
        label: `${sourceFormat.toUpperCase()} file`,
        accept: `.${sourceFormat}`,
        extensions: [sourceFormat],
      };
    }
    return { label: 'supported file', accept: '.csv,.xlsx,.xls', extensions: ['csv', 'xlsx', 'xls'] };
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.assignFile(input.files?.[0] ?? null);
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.assignFile(event.dataTransfer?.files?.[0] ?? null);
  }

  removeSelectedFile(): void {
    if (this.uploading || this.fetchingAssets) return;
    this.selectedFile = null;
    this.errorMessage = '';
    this.importMessage = '';
    this.assets = [];
    this.assetsLoaded = false;
  }

  /**
   * Upload the selected file, then fetch the records created by the import.
   * Observable subscriptions keep this compatible with Angular 16 projects
   * that use RxJS 6 or RxJS 7.
   */
  uploadAndFetchAssets(): void {
    const assetSourceId = String(this.data?.assetSourceId ?? '').trim();
    if (!this.selectedFile || !assetSourceId || this.uploading || this.fetchingAssets) {
      if (!assetSourceId) {
        this.errorMessage = 'Asset source ID is missing. Reopen the dialog from a valid data asset source.';
      }
      return;
    }

    this.uploading = true;
    this.fetchingAssets = false;
    this.assetsLoaded = false;
    this.assets = [];
    this.errorMessage = '';
    this.importMessage = '';

    this.dataAssetSourceService.ImportDataAssetsFromFile(this.selectedFile, assetSourceId).subscribe({
      next: response => {
        this.importMessage = this.readMessage(response) ||
          'File uploaded successfully. Fetching imported data assets…';
        this.fetchingAssets = true;

        this.dataAssetSourceService.FetchDataAssetsFromFile(assetSourceId).subscribe({
          next: fetchResponse => {
            this.assets = this.normalizeAssets(fetchResponse);
            this.assetsLoaded = true;
            this.importMessage = `Import completed. ${this.assets.length} data asset(s) fetched.`;
            this.uploading = false;
            this.fetchingAssets = false;
          },
          error: error => {
            this.errorMessage = this.extractMessage(error) ||
              'The file was uploaded, but the imported data assets could not be fetched.';
            this.importMessage = '';
            this.uploading = false;
            this.fetchingAssets = false;
          },
        });
      },
      error: error => {
        this.errorMessage = this.extractMessage(error) ||
          'Unable to import the selected file. Check the file format and try again.';
        this.importMessage = '';
        this.uploading = false;
        this.fetchingAssets = false;
      },
    });
  }

  formatFileSize(size: number): string {
    if (!Number.isFinite(size) || size < 0) return 'Unknown size';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  assetValue(asset: AssetManagement, ...keys: string[]): string {
    const record = asset as unknown as AssetRecord;
    for (const key of keys) {
      const formatted = this.formatCellValue(record[key]);
      if (formatted) return formatted;
    }
    return '—';
  }

  close(): void {
    if (this.uploading || this.fetchingAssets) return;
    this.dialogRef.close({ imported: this.assetsLoaded });
  }

  private assignFile(file: File | null): void {
    if (this.uploading || this.fetchingAssets) return;
    this.errorMessage = '';
    this.importMessage = '';
    this.assets = [];
    this.assetsLoaded = false;

    if (!file) {
      this.selectedFile = null;
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (this.formatConfig.extensions.length && !this.formatConfig.extensions.includes(extension)) {
      this.selectedFile = null;
      this.errorMessage = `Unsupported file type. Please select a ${this.formatConfig.label}.`;
      return;
    }
    this.selectedFile = file;
  }

  private normalizeAssets(response: unknown): AssetManagement[] {
    if (Array.isArray(response)) return response as AssetManagement[];
    if (!response || typeof response !== 'object') return [];

    const payload = response as Record<string, unknown>;
    const arrayKeys = [
      'dataAssets', 'assets', 'data', 'results', 'items', 'content', 'records', 'rows',
      'assetList', 'dataAssetList', 'dataAssetsList', 'dataAssetDTOs', 'assetManagements',
    ];
    for (const key of arrayKeys) {
      if (Array.isArray(payload[key])) return payload[key] as AssetManagement[];
    }

    for (const key of ['payload', 'response', 'result', 'data']) {
      const nested = payload[key];
      if (nested && typeof nested === 'object') {
        const normalized = this.normalizeAssets(nested);
        if (normalized.length > 0) return normalized;
      }
    }

    const assetKeys = [
      'DATAAssetName', 'dataAssetName', 'dataAssetId', 'DATAAssetId', 'assetName', 'assetId',
      'name', 'qualifiedName', 'uniqueIdentifier',
    ];
    if (assetKeys.some((key) => payload[key] !== undefined)) return [payload as unknown as AssetManagement];
    return [];
  }

  private formatCellValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    if (Array.isArray(value)) {
      const entries = value.map(item => this.formatCellValue(item)).filter(Boolean);
      return entries.length ? entries.join(', ') : '';
    }
    if (typeof value === 'object') {
      const objectValue = value as Record<string, unknown>;
      return typeof objectValue['name'] === 'string' ? objectValue['name'] : '';
    }

    const text = String(value).trim();
    if (!text || text === '[]' || text === '{}' || text.toLowerCase() === 'null') return '';
    if ((text.startsWith('[') && text.endsWith(']')) || (text.startsWith('{') && text.endsWith('}'))) {
      try {
        const parsed: unknown = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed.map(item => this.formatCellValue(item)).filter(Boolean).join(', ');
        }
        if (parsed && typeof parsed === 'object') {
          const obj = parsed as Record<string, unknown>;
          if (typeof obj['name'] === 'string') return obj['name'];
        }
      } catch {
        // If the value is not valid JSON, preserve it as-is.
      }
    }
    return text;
  }

  private readMessage(value: unknown): string {
    if (typeof value === 'string') return value;
    if (!value || typeof value !== 'object') return '';
    const record = value as Record<string, unknown>;
    return typeof record['message'] === 'string' ? record['message'] : '';
  }

  private extractMessage(value: unknown): string {
    if (typeof value === 'string') return value;
    if (!value || typeof value !== 'object') return '';
    const record = value as Record<string, unknown>;
    const error = record['error'];
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object') {
      const nested = error as Record<string, unknown>;
      if (typeof nested['message'] === 'string') return String(nested['message']);
    }
    return typeof record['message'] === 'string' ? String(record['message']) : '';
  }
}
